import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'landlord-tenant-documents';
const PDF_BUCKET_NAME = 'document-pdfs';

const PDF_SIGNED_URL_EXPIRY_SECONDS = 3600; // 1 hour

export interface PdfUploadResult {
  path: string;
  signedUrl: string;
}

export const uploadPdfToStorage = async (
  documentId: string,
  pdfBlob: Blob
): Promise<PdfUploadResult> => {
  const filePath = `${documentId}/document.pdf`;

  const { data, error } = await supabase.storage
    .from(PDF_BUCKET_NAME)
    .upload(filePath, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true,
    });

  if (error) {
    throw new Error(`PDF upload failed: ${error.message}`);
  }

  const { data: signedData, error: signedError } = await supabase.storage
    .from(PDF_BUCKET_NAME)
    .createSignedUrl(data.path, PDF_SIGNED_URL_EXPIRY_SECONDS);

  if (signedError || !signedData?.signedUrl) {
    throw new Error(`Failed to create signed URL: ${signedError?.message}`);
  }

  return {
    path: data.path,
    signedUrl: signedData.signedUrl,
  };
};

export const getPdfSignedUrl = async (
  path: string,
  expiresInSeconds: number = PDF_SIGNED_URL_EXPIRY_SECONDS
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(PDF_BUCKET_NAME)
    .createSignedUrl(path, expiresInSeconds);

  if (error || !data?.signedUrl) {
    throw new Error(`Failed to create signed URL: ${error?.message}`);
  }

  return data.signedUrl;
};

export interface DocumentUploadResult {
  path: string;
  url: string;
}

export const uploadDocument = async (
  documentId: string,
  tier: 'standard' | 'enhanced' | 'premium',
  content: string
): Promise<DocumentUploadResult> => {
  try {
    const fileName = `${documentId}/${tier}.txt`;
    const blob = new Blob([content], { type: 'text/plain' });

    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(fileName, blob, {
        contentType: 'text/plain',
        upsert: true,
      });

    if (error) {
      console.warn('Storage bucket not available, using fallback storage:', error);
      // Fallback: store in memory for this session
      const fallbackPath = `temp_${Date.now()}/${tier}.txt`;
      const fallbackUrl = `data:text/plain;base64,${btoa(content)}`;

      return {
        path: fallbackPath,
        url: fallbackUrl,
      };
    }

    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(fileName);

    return {
      path: data.path,
      url: urlData.publicUrl,
    };
  } catch (error) {
    console.warn('Storage upload failed, using fallback:', error);
    // Fallback: store in memory for this session
    const fallbackPath = `temp_${Date.now()}/${tier}.txt`;
    const fallbackUrl = `data:text/plain;base64,${btoa(content)}`;

    return {
      path: fallbackPath,
      url: fallbackUrl,
    };
  }
};

export const downloadDocument = async (path: string): Promise<string> => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .download(path);

    if (error) throw error;

    const text = await data.text();
    return text;
  } catch (error) {
    console.error('Error downloading document:', error);
    throw new Error(`Failed to download document: ${error}`);
  }
};

export const deleteDocument = async (path: string): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([path]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw new Error(`Failed to delete document: ${error}`);
  }
};

export const listDocuments = async (documentId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(documentId);

    if (error) throw error;

    return data.map((file) => `${documentId}/${file.name}`);
  } catch (error) {
    console.error('Error listing documents:', error);
    return [];
  }
};
